/* ============================================================
   SPD Test-Instrument Hero — telemetry presets
   Keyed by SKU-class. A merchandiser points a hero at a class
   and gets correct ranges; model/meta/copy override per section.

   IMPORTANT (spec §7): ranges below are realistic starting
   points seeded from the build spec. Verify against the real
   Grafana / burn-in dashboards per product line BEFORE go-live.
   The GPU class is already tuned to the n144 fleet.

   Stat value types (map + checklist `stats`/`aggregate`):
     progress            — sweep completion %          fmt {v.N}
     count {total}       — "n / total" as sweep fills
     passCount           — "pass / passes" (multi-pass RAM)
     lerp {from,to}      — follows progress            fmt {v.N}
     climb {from,rise,wobble} — rises with progress + sine wobble
     jitter {idle,base,amp}   — ramps idle→base on the load
                            timeline, then wanders ±amp
     pair {a,b}          — two jitters, fmt "{a.0}°C / {b.0}°C"
     timer {hours}       — accelerated soak clock hh:mm:ss
     fixed {value}       — constant string; ok:true renders green
   ============================================================ */
window.SPDHeroPresets = {

  /* ---------------- HDD — reference archetype ---------------- */
  'hdd-exos-x24': {
    archetype: 'map',
    label: 'FULL-SPAN SURFACE READ',
    model: 'Seagate Exos X24 — 24TB SATA',
    meta: 'ST24000NM000H · S/N ZR7•••K2 · bench 04 · lot 2306',
    states: ['SCANNING', 'COMPLETE'],
    verdict: { active: 'IN PROGRESS', pass: 'PASS · 0 ERRORS' },
    footer: 'SMART: reallocated 0 · pending 0 · POH 18,402',
    ariaLabel: 'Live demonstration of a full-span surface read: a sector map of a 24-terabyte Seagate Exos drive filling in as every sector is read, finishing with zero errors and a PASS verdict.',
    map: {
      mode: 'sweep',
      cols: 28, rows: 22,
      fillMs: 6500, holdMs: 1900,
      passes: 1,
      cellNoun: 'sector',
      stats: [
        { label: 'SECTORS', type: 'progress', fmt: '{v.1}%' },
        { label: 'SPEED',   type: 'lerp', from: 285, to: 160, fmt: '{v.0} MB/s' },   /* outer → inner tracks */
        { label: 'TEMP',    type: 'climb', from: 38, rise: 6, wobble: 0.45, fmt: '{v.1}°C' },
        { label: 'ERRORS',  type: 'fixed', value: '0', ok: true }
      ]
    }
  },

  /* ---------------- SSD / NVMe — fast sweep ---------------- */
  'ssd-nvme-u2-gen4': {
    archetype: 'map',
    label: 'FULL-SPAN VERIFY + ENDURANCE',
    model: 'Kioxia CD6-R 7.68TB U.2 NVMe',
    meta: 'KCD61LUL7T68 · Gen4 U.2 · S/N 91Q•••77 · bench 11',
    states: ['VERIFYING', 'COMPLETE'],
    verdict: { active: 'IN PROGRESS', pass: 'PASS' },
    footer: 'Uncorrectable 0 · available spare 100% · 1 DWPD class',
    ariaLabel: 'Live demonstration of a full-span NAND verify on a 7.68-terabyte U.2 NVMe drive: the block map fills at roughly 6,800 megabytes per second, finishing with zero uncorrectable errors — a PASS verdict.',
    map: {
      mode: 'sweep',
      cols: 28, rows: 22,
      fillMs: 3600, holdMs: 1600,      /* NAND verify is fast — that IS the story */
      passes: 1,
      cellNoun: 'NAND block',
      stats: [
        { label: 'VERIFY', type: 'progress', fmt: '{v.1}%' },
        { label: 'SPEED',  type: 'climb', from: 6790, rise: 0, wobble: 55, fmt: '{v.0} MB/s' },
        { label: 'TEMP',   type: 'climb', from: 45, rise: 17, wobble: 0.5, fmt: '{v.0}°C' }
      ]
    }
  },

  /* ---------------- RAM — live bench console (concept D) ---------------- */
  'ram-ddr4-3200-rdimm': {
    archetype: 'console',
    label: 'BENCH 05 · LIVE LOG',
    model: '64GB DDR4-3200 ECC RDIMM',
    meta: '2Rx4 · slot A4 of 24 · in-server, full bank · lot 2412',
    states: ['RUNNING', 'DONE'],
    verdict: { active: 'IN PROGRESS', pass: 'PASS' },
    footer: 'ECC: corrected 0 · uncorrectable 0 · log ships with listing',
    ariaLabel: 'A live memory test console: pattern passes stream by region by region, ECC counters stay at zero throughout, and the log ends with a PASS result and zero errors.',
    console: {
      visible: 9,
      passes: 4,
      holdMs: 2600,
      /* [delayMs since previous line, css class, text] — running
         timestamps are prepended live */
      script: [
        [300, 'hd', 'SPD OK - Samsung M393A8G40AB2-CWE - 64GB 2Rx4'],
        [400, 'hd', 'PASS 1/4 - moving inversions'],
        [460, 'ad', '0x0_0000_0000-0x0_FFFF_FFFF .. OK'],
        [460, 'ad', '0x1_0000_0000-0x1_FFFF_FFFF .. OK'],
        [460, 'ad', '0x2_0000_0000-0x2_FFFF_FFFF .. OK'],
        [460, 'ad', '0x3_0000_0000-0x3_FFFF_FFFF .. OK'],
        [320, '',   'ECC: corrected 0 - uncorrectable 0'],
        [400, 'hd', 'PASS 2/4 - walking ones'],
        [460, 'ad', '0x0_0000_0000-0x0_FFFF_FFFF .. OK'],
        [460, 'ad', '0x1_0000_0000-0x1_FFFF_FFFF .. OK'],
        [460, 'ad', '0x2_0000_0000-0x2_FFFF_FFFF .. OK'],
        [460, 'ad', '0x3_0000_0000-0x3_FFFF_FFFF .. OK'],
        [320, '',   'ECC: corrected 0 - uncorrectable 0'],
        [400, 'hd', 'PASS 3/4 - addr-in-addr'],
        [460, 'ad', '0x0_0000_0000-0x0_FFFF_FFFF .. OK'],
        [460, 'ad', '0x1_0000_0000-0x1_FFFF_FFFF .. OK'],
        [460, 'ad', '0x2_0000_0000-0x2_FFFF_FFFF .. OK'],
        [460, 'ad', '0x3_0000_0000-0x3_FFFF_FFFF .. OK'],
        [320, '',   'ECC: corrected 0 - uncorrectable 0'],
        [400, 'hd', 'PASS 4/4 - random pattern'],
        [460, 'ad', '0x0_0000_0000-0x0_FFFF_FFFF .. OK'],
        [460, 'ad', '0x1_0000_0000-0x1_FFFF_FFFF .. OK'],
        [460, 'ad', '0x2_0000_0000-0x2_FFFF_FFFF .. OK'],
        [460, 'ad', '0x3_0000_0000-0x3_FFFF_FFFF .. OK'],
        [320, '',   'ECC: corrected 0 - uncorrectable 0'],
        [420, 'ok', 'RESULT: PASS - 0 errors - 4/4 patterns clean']
      ],
      stats: [
        { label: 'PASS',   type: 'passCount' },
        { label: 'TEMP',   type: 'climb', from: 38, rise: 6.1, wobble: 0.2, fmt: '{v.1}°C' },
        { label: 'ERRORS', type: 'fixed', value: '0', ok: true }
      ]
    },
    /* alternate instrument: rank-split address map with write/verify
       passes — flip the section's Instrument override to "Map" */
    map: {
      mode: 'sweep',
      cols: 32, rows: 8,
      banks: 2,                         /* dual-rank: two labelled grids */
      bankLabels: ['RANK 0', 'RANK 1'],
      alternate: true,                  /* odd passes walk the address space backwards */
      verify: true,                     /* write sweep, then cyan read-back/ECC sweep */
      fillMs: 3000, holdMs: 1900,
      passes: 4,                        /* write+verify → clear → repeat, counter in meta */
      cellNoun: 'address block',
      stats: [
        { label: 'COVERAGE',   type: 'progress', fmt: '{v.1}%' },
        { label: 'PASS',       type: 'passCount' },
        /* every 72-bit read is syndrome-checked: 64GiB / 8B = 8.6G words × 4 passes */
        { label: 'ECC WORDS',  type: 'counter', target: 34.4e9 },
        { label: 'BIT ERRORS', type: 'fixed', value: '0', ok: true }
      ]
    }
  },

  /* ---------------- JBOD — bay semantics ---------------- */
  'jbod-90bay': {
    archetype: 'map',
    label: 'PARALLEL R/W EXERCISE',
    model: '90-Bay 4U SAS JBOD — fully populated',
    meta: 'write / read / verify · 90 drives in parallel · rack 07',
    states: ['R/W EXERCISE', 'COMPLETE'],
    verdict: { active: 'IN PROGRESS', pass: 'ALL BAYS OK' },
    footer: 'Every bay populated · SMART clean × 90',
    ariaLabel: 'Live demonstration of a parallel read-write exercise across a 90-bay JBOD: every populated bay\'s activity light flickers as its drive is written and verified, bays settling to steady green as each drive passes, finishing with all 90 bays OK.',
    map: {
      mode: 'exercise',                /* all bays under load; verify in random order */
      cols: 18, rows: 5,               /* 90 bays — tall caddy cells read as a chassis */
      cellStyle: 'bay',                /* slot bezel + activity/status LED per bay */
      fillMs: 6500, holdMs: 2000,
      passes: 1,
      cellNoun: 'bay',
      stats: [
        { label: 'BAYS',    type: 'count', total: 90 },
        { label: 'AGG R/W', type: 'climb', from: 26, rise: 2, wobble: 0.4, fmt: '{v.1} GB/s' },
        { label: 'TEMP',    type: 'climb', from: 24, rise: 8, wobble: 0.5, fmt: '{v.0}°C' },
        { label: 'ERRORS',  type: 'fixed', value: '0', ok: true }
      ]
    }
  },

  /* ---------------- CPU — core bank under all-core load ---------------- */
  'cpu-epyc-7763': {
    archetype: 'map',
    label: 'ALL-CORE BURN-IN',
    model: 'AMD EPYC 7763 — 64C / 128T',
    meta: 'SP3 · 280W TDP · sustained all-core stress · bench 22',
    states: ['POWER-ON', 'LOAD RAMP', 'ALL-CORE SOAK', 'DONE'],
    verdict: { active: 'IN PROGRESS', pass: 'PASS · 0 MCE' },
    footer: 'MCE 0 · all cores stable · TDP 280 W',
    ariaLabel: 'Live demonstration of a sustained all-core burn-in on a 64-core AMD EPYC 7763: all 64 core cells pinned near 100 percent load at a stable 3.05 gigahertz all-core clock and 79 degrees Celsius package temperature, with zero machine-check errors — a PASS verdict.',
    map: {
      mode: 'bank',
      cols: 16, rows: 4,               /* one cell per core — the core count IS the flex */
      ramp: { startMs: 500, endMs: 2300, settleMs: 4200 },
      cellNoun: 'core',
      stats: [
        { label: 'ALL-CORE',  type: 'jitter', idle: 2,    base: 99.8, amp: 0.3,  fmt: '{v.1}%' },
        { label: 'CLOCK',     type: 'jitter', idle: 2.45, base: 3.05, amp: 0.05, fmt: '{v.2} GHz' },  /* base 2.45 / boost 3.5 */
        { label: 'TEMP',      type: 'jitter', idle: 42,   base: 79,   amp: 0.8,  fmt: '{v.0}°C' },
        { label: 'PKG POWER', type: 'jitter', idle: 68,   base: 279,  amp: 4,    fmt: '{v.0} W' }
      ]
    },
    /* graph fallback (spec §7): if the core bank reads too busy on
       mobile, flip archetype override to "graph" in the section. */
    graph: {
      traces: [
        { key: 'load',  label: 'ALL-CORE', color: 'var(--green-bright)', colorHex: '#46c25c', range: [0, 100],    idle: 2,    soakTarget: 99.8, jitter: { ramp: 0.7,  soak: 0.35 }, fmt: '{v.1}%' },
        { key: 'clock', label: 'CLOCK',    color: 'var(--cyan)',         colorHex: '#5ab0d6', range: [2.2, 3.6],  idle: 2.45, soakTarget: 3.05, jitter: { ramp: 0.02, soak: 0.03 }, fmt: '{v.2} GHz' },
        { key: 'temp',  label: 'TEMP',     color: 'var(--amber-2)',      colorHex: '#e7a92a', range: [30, 95],    idle: 42,   soakTarget: 79,   jitter: { ramp: 0.5,  soak: 0.6 },  fmt: '{v.0}°C' },
        { key: 'power', label: 'PKG POWER',color: 'var(--violet)',       colorHex: '#b98bd9', range: [50, 300],   idle: 68,   soakTarget: 279,  jitter: { ramp: 2,    soak: 3 },    fmt: '{v.0} W' }
      ],
      ramp: { startMs: 500, endMs: 2300, settleMs: 4200 }
    }
  },

  /* ---------------- GPU — reference archetype (n144-tuned) ---------------- */
  'gpu-a100-80': {
    archetype: 'graph',
    label: 'BATCH BURN-IN',
    model: 'NVIDIA A100 80GB PCIe',
    meta: 'node n144 · SM soak 10h 20m · S/N 1650•••042',
    states: ['POWER-ON', 'LOAD RAMP', 'FULL-LOAD SOAK', 'DONE'],
    verdict: { active: 'IN PROGRESS', pass: 'PASS' },
    footer: 'ECC: uncorrectable 0 · corrected 0 · mem 1215 MHz · {power} W',
    ariaLabel: 'Live full-load burn-in of an NVIDIA A100 80GB: utilization, VRAM and temperature traces ramp to a stable full-load soak — 100 percent utilization and 82 degrees Celsius — finishing with zero ECC errors and a PASS verdict.',
    graph: {
      traces: [
        { key: 'util',  label: 'UTIL',     color: 'var(--green-bright)', colorHex: '#46c25c', range: [0, 100],    idle: 3,   soakTarget: 100,  jitter: { ramp: 0.6,  soak: 0.4 },  fmt: '{v.1}%' },
        { key: 'vram',  label: 'VRAM',     color: 'var(--violet)',       colorHex: '#b98bd9', range: [0, 100],    idle: 4,   soakTarget: 100,  jitter: { ramp: 0.25, soak: 0 },    fmt: '{gb} GB', gbOf: 80 },
        { key: 'temp',  label: 'TEMP',     color: 'var(--amber-2)',      colorHex: '#e7a92a', range: [25, 95],    idle: 33,  soakTarget: 82,   jitter: { ramp: 0.45, soak: 0.45 }, fmt: '{v.1}°C' }
      ],
      power: { idle: 52, soakTarget: 245, jitter: { ramp: 2, soak: 4 } },   /* footer {power} */
      ramp: { startMs: 500, endMs: 2100, settleMs: 4000 }
    }
  },

  /* ---------------- Server — chassis map (concept S7) ---------------- */
  'server-r750': {
    archetype: 'chassis',
    label: 'CHASSIS VALIDATION',
    model: 'Dell PowerEdge R750 — 2× Xeon Gold 6338',
    meta: '64C/128T · 512GB DDR4 · 12 bays · bench 09 · lot 2407',
    states: ['POWER-ON', 'STORAGE', 'COOLING', 'MEMORY', 'COMPUTE', 'NETWORK', 'POWER', 'DONE'],
    verdict: { active: 'IN PROGRESS', pass: 'PASS' },
    footer: 'BMC OK · SEL clean · ECC 0 · MCE 0',
    ariaLabel: 'A top-down diagram of a two-socket server: drive bays, fan wall, sixteen DIMMs, both CPUs, network and both power supplies light up amber under test one group at a time, each turning green as it validates, until the whole chassis is green and the unit passes.',
    chassis: {
      bays: [3, 4],                     /* 12 bays, front-left */
      fans: 6,
      dimmsPerSide: 4,                  /* × 2 sides × 2 sockets = 16 DIMMs */
      labels: ['12 BAYS', 'FANS', 'CPU0 · CPU1 · 16 DIMM', 'PSU · NIC · BMC'],
      holdMs: 2600,
      stats: [
        { label: 'SUBSYS',     type: 'count', total: 6 },
        { label: 'POWER',      type: 'jitter', idle: 112, base: 590, amp: 4, fmt: '{v.0} W' },
        { label: 'EXHAUST',    type: 'jitter', idle: 24, base: 41.2, amp: 0.2, fmt: '{v.1}°C' },
        { label: 'SEL EVENTS', type: 'fixed', value: '0', ok: true }
      ]
    },
    /* alternate instrument: subsystem checklist + 48h soak clock —
       flip the section's Instrument override to "Checklist" */
    checklist: {
      soakLabel: 'COMBINED STRESS SOAK',
      soakHours: 48,                    /* spec §7: use 48h of the 24–72h window */
      postMs: 600, itemMs: 420, soakMs: 4600, holdMs: 2800,
      items: [
        { label: 'CPUs',   value: '2 / 2' },
        { label: 'DIMMs',  value: '24 / 24' },
        { label: 'Drives', value: '12 / 12' },
        { label: 'PSUs',   value: '2 / 2 · redundant' },
        { label: 'NICs',   value: '4 × 25GbE' },
        { label: 'iDRAC',  value: 'OK · log clean' },
        { label: 'POST',   value: 'OK' }
      ],
      aggregate: [
        { label: 'SOAK',            type: 'timer', hours: 48 },
        { label: 'POWER',           type: 'jitter', idle: 180, base: 585, amp: 8, fmt: '{v.0} W' },
        { label: 'INLET / EXHAUST', type: 'pair', a: { idle: 21, base: 21, amp: 0.3 }, b: { idle: 24, base: 40, amp: 0.6 }, fmt: '{a.0}°C / {b.0}°C' },
        { label: 'FANS',            type: 'jitter', idle: 4200, base: 9800, amp: 160, fmt: '{v.0} RPM' }
      ]
    }
  }
};

/* if spd-hero.js ran first and found no presets, re-init now —
   makes the two files order-independent regardless of loader */
if (window.SPDHero) window.SPDHero.initAll();

